import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import CircularProgress from "@mui/material/CircularProgress"
import CssBaseline from "@mui/material/CssBaseline"
import Link from "@mui/material/Link"
import Paper from "@mui/material/Paper"
import Typography from "@mui/material/Typography"
import { useEffect, useMemo, useState } from "react"
import { useParams, useSearchParams } from "react-router-dom"

import Column from "../../common/components/Column"
import BuyMoreMlsDialog from "../customer/my-mls/BuyMoreMlsDialog"
import MlsPurchaseResultBanner from "../customer/my-mls/MlsPurchaseResultBanner"
import { MLS_ACTIVATION_TIMELINE } from "../customer/my-mls/mlsPurchaseActivationCopy"
import AccountHeaderBar, { ACCOUNT_HEADER_OFFSET } from "./AccountHeaderBar"
import { createMlsPurchaseLinkClient, linkErrorMessage } from "./linkClient"

const POLL_MS = 5000
const MAX_POLLS = 40

function Shell({ header = null, children }) {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        pt: ACCOUNT_HEADER_OFFSET,
        bgcolor: "background.default",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
      }}
    >
      <CssBaseline />
      {header}
      <Paper variant="outlined" sx={{ p: 4, maxWidth: 520, width: "100%" }}>
        <Column sx={{ gap: 2, alignItems: "center", textAlign: "center" }}>
          {children}
        </Column>
      </Paper>
    </Box>
  )
}

function PurchaseResult({ client, purchaseId }) {
  const [purchase, setPurchase] = useState(null)
  const [error, setError] = useState("")

  useEffect(() => {
    let cancelled = false
    let attempts = 0
    let timer = null

    const poll = () => {
      client
        .get(`/billing/api/mls-purchases/${purchaseId}/`)
        .then(({ data }) => {
          if (cancelled) return
          setPurchase(data)
          setError("")
          if (
            data.assignment_status === "assigned" ||
            data.assignment_status === "failed" ||
            attempts >= MAX_POLLS
          ) {
            return
          }
          attempts += 1
          timer = setTimeout(poll, POLL_MS)
        })
        .catch((err) => {
          if (cancelled) return
          const statusCode = err?.response?.status
          if (statusCode === 401 || statusCode === 404) {
            setError(linkErrorMessage(err))
            return
          }
          if (attempts >= MAX_POLLS) return
          attempts += 1
          timer = setTimeout(poll, POLL_MS)
        })
    }

    poll()
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [client, purchaseId])

  if (error) {
    return <Typography color="text.secondary">{error}</Typography>
  }
  if (!purchase) {
    return <CircularProgress />
  }
  if (purchase.status !== "paid") {
    return (
      <>
        <CircularProgress />
        <Typography color="text.secondary">
          Confirming your payment… this can take a minute.
        </Typography>
      </>
    )
  }
  return (
    <>
      <CheckCircleOutlineIcon color="success" sx={{ fontSize: 48 }} />
      <Typography variant="h6" fontWeight={700}>
        Payment received
      </Typography>
      <Box sx={{ width: "100%", textAlign: "left" }}>
        <MlsPurchaseResultBanner purchase={purchase} />
      </Box>
      <Typography variant="body2" color="text.secondary">
        {MLS_ACTIVATION_TIMELINE}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        You can close this tab.
      </Typography>
    </>
  )
}

/**
 * Standalone "buy your MLS" page opened from a link SmartSetter sends an
 * account owner. It never logs them in: the link only works on the Purchase
 * MLS APIs, and closing the form leaves them here, not in the app.
 */
export default function BuyMlsPage() {
  const { signature: rawSignature } = useParams()
  const [searchParams] = useSearchParams()
  const signature = useMemo(
    () => decodeURIComponent(rawSignature || ""),
    [rawSignature]
  )
  const client = useMemo(
    () => createMlsPurchaseLinkClient(signature),
    [signature]
  )
  const purchaseId =
    searchParams.get("mls_purchase") === "success"
      ? searchParams.get("purchase_id")
      : null
  const [state, setState] = useState(purchaseId ? "result" : "checking")
  const [message, setMessage] = useState("")
  const [savedForPayer, setSavedForPayer] = useState(false)
  const [account, setAccount] = useState({})

  // Whose account this is, shown even when the link has expired or been used.
  useEffect(() => {
    let cancelled = false
    client
      .get("/billing/api/mls-purchase-link/identity/")
      .then(({ data }) => {
        if (!cancelled) setAccount(data || {})
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [client])
  const header = (
    <AccountHeaderBar
      accountName={account.account_name}
      accountEmail={account.account_email}
    />
  )

  useEffect(() => {
    if (purchaseId) return undefined
    let cancelled = false
    client
      .get("/billing/api/mls-addon-offer/")
      .then(({ data }) => {
        if (cancelled) return
        if (data?.checkout_blocked_reason) {
          setMessage(data.checkout_blocked_reason)
          setState("unavailable")
          return
        }
        setState("form")
      })
      .catch((err) => {
        if (cancelled) return
        setMessage(linkErrorMessage(err))
        setState("unavailable")
      })
    return () => {
      cancelled = true
    }
  }, [client, purchaseId])

  if (state === "result") {
    return (
      <Shell header={header}>
        <PurchaseResult client={client} purchaseId={purchaseId} />
      </Shell>
    )
  }

  if (state === "checking") {
    return (
      <Shell header={header}>
        <CircularProgress />
      </Shell>
    )
  }

  if (state === "unavailable") {
    return (
      <Shell header={header}>
        <Typography variant="h6" fontWeight={700}>
          Purchase MLS
        </Typography>
        <Typography color="text.secondary">{message}</Typography>
        <Link href="/user/log-in">Log in to OneBase</Link>
      </Shell>
    )
  }

  if (state === "closed") {
    return (
      <Shell header={header}>
        <Typography variant="h6" fontWeight={700}>
          Purchase MLS
        </Typography>
        <Typography color="text.secondary">
          {savedForPayer
            ? "Your order is saved and waiting for payment from the person you sent the link to. You can close this tab."
            : "Nothing was purchased. You can close this tab, or pick up where you left off."}
        </Typography>
        <Button variant="contained" onClick={() => setState("form")}>
          {savedForPayer ? "Start another order" : "Back to the form"}
        </Button>
      </Shell>
    )
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <CssBaseline />
      {header}
      <BuyMoreMlsDialog
        open
        standalone
        client={client}
        onClose={(reason) => {
          setSavedForPayer(Boolean(reason?.savedForPayer))
          setState("closed")
        }}
      />
    </Box>
  )
}
